import { registerPlugin } from "@capacitor/core";
import { isNative } from "./platform.js";

/**
 * Puente al plugin nativo `AppSettings` (android/.../AppSettingsPlugin.java):
 * las pantallas del sistema que la app no puede reemplazar por código.
 *
 * Vive todo acá —el registro y los tres envoltorios— porque son la misma cosa:
 * llevar al usuario a una pantalla de Android. Estuvieron repartidos entre este
 * módulo y `pushMobile.js`, con el mismo try/catch escrito tres veces, hasta
 * que quedó claro que "abrir los ajustes de notificaciones" no es un asunto de
 * push más de lo que "abrir la ficha de la app" es un asunto de ubicación.
 * `pushMobile.js` re-exporta los dos de notificaciones para no mover imports.
 */
export const AppSettings = registerPlugin("AppSettings");

/**
 * Abre una pantalla del sistema.
 *
 * @returns {Promise<boolean>} false cuando no se pudo, y ahí el llamador tiene
 *   que dictar el camino a mano: un botón que dice que abre algo y no abre nada
 *   es peor que no tenerlo. Las causas reales son dos — web (no hay ajustes de
 *   app que abrir) e iOS mientras no exista el proyecto nativo—, más un ROM sin
 *   la actividad de ajustes, que hace que `launch()` rechace con
 *   ActivityNotFoundException.
 *
 *   Lo que NO puede pasar es que al APK le falte el método: `capacitor.config.ts`
 *   no define `server.url`, así que el bundle web viaja DENTRO del mismo APK que
 *   el Java y los dos se actualizan juntos, siempre.
 */
async function openSettingsScreen(call) {
  if (!isNative()) return false;
  try {
    await call();
    return true;
  } catch {
    return false;
  }
}

/**
 * Ficha de la app en los ajustes del sistema — la pantalla donde se conceden
 * los permisos que el usuario ya rechazó.
 *
 * Android deja de preguntar tras dos rechazos: `requestPermissions()` resuelve
 * "denied" al instante y sin mostrar nada, así que un botón que solo reintenta
 * pide para siempre un permiso que nunca se va a conceder. Esta es la salida.
 */
export function openAppSettings() {
  return openSettingsScreen(() => AppSettings.openAppSettings());
}

/** Ajustes de notificación de esta app (todos sus canales). */
export function openNotificationSettings() {
  return openSettingsScreen(() => AppSettings.openNotificationSettings());
}

/**
 * Ajustes de UN canal de alerta — la pantalla donde el sistema deja elegir el
 * tono y la vibración. Ninguna API permite cambiarlos por código: son del
 * usuario a propósito, así que lo máximo que se puede hacer es aterrizarlo en
 * el lugar correcto.
 *
 * @param {"urgent"|"info"} channel
 */
export function openAlertChannelSettings(channel = "urgent") {
  return openSettingsScreen(() => AppSettings.openChannelSettings({ channel }));
}
